import { useAuth } from "@clerk/clerk-react";
import type {
	API_CreateInvoiceLineItemRequest,
	API_CreateInvoiceRequest,
	API_CreateOfflinePaymentRequest,
	API_GetInvoiceResponse,
	Invoice,
	PaymentMethod,
} from "@pilatestime/shared-types";
import {
	Currency,
	InvoiceLineItemType,
	parseCalendarDate,
} from "@pilatestime/shared-types";
import {
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	fetchInvoiceByIdQueryOptions,
	fetchProviderAccountsQueryOptions,
	useCreateInvoice,
	useCreateOfflinePayment,
} from "@/api/billing";
import { fetchCustomerByIdQueryOptions } from "@/api/customers";
import { fetchEnrollmentByIdQueryOptions } from "@/api/enrollments";
import { PageHeader } from "@/components/PageHeader";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import { parseCurrencyInput } from "@/utils/utils";
import {
	InvoiceStepperHeader,
	InvoiceStepperNavigation,
	useInvoiceWizard,
} from "../-components/InvoiceStepper";
import { InvoiceStep } from "../-components/wizard/InvoiceStep";
import { PaymentStep } from "../-components/wizard/PaymentStep";
import {
	createInitialWizardState,
	type InvoiceFormData,
	type InvoiceWizardState,
	type PaymentFormData,
} from "../-components/wizard/types";

// Render a CalendarDate ("YYYY-MM-DD") as a localized day, pinned to UTC so the
// day baked into a persisted line-item description never shifts for viewers west
// of Greenwich (design P1-c). No local-midnight `new Date(dayString)`.
const formatCalendarDayLocale = (day: string, locale: string): string =>
	new Date(
		Date.UTC(
			Number(day.slice(0, 4)),
			Number(day.slice(5, 7)) - 1,
			Number(day.slice(8, 10)),
		),
	).toLocaleDateString(locale, { timeZone: "UTC" });

const billingNewSearchSchema = z.object({
	enrollmentId: z.string().optional(),
	invoiceId: z.string().optional(),
	customerId: z.string().optional(),
	renewal: z
		.union([z.boolean(), z.string()])
		.transform((val) => val === true || val === "true")
		.optional(),
	periodStart: z.string().optional(),
	periodEnd: z.string().optional(),
});

const mapInvoiceToFormData = (
	invoice: API_GetInvoiceResponse,
): InvoiceFormData => {
	const currency = invoice.currency as Currency;
	const locale = currency === Currency.BRL ? "pt-BR" : "en-US";

	return {
		customerId: invoice.customer?.id ?? "",
		customerName: invoice.customer
			? `${invoice.customer.firstName} ${invoice.customer.lastName}`
			: "",
		customerEmail: invoice.customer?.email,
		currency,
		// dueDate is already a CalendarDate ("YYYY-MM-DD") — the form input format —
		// so it flows straight through with no `new Date`, no TZ shift.
		dueDate: invoice.dueDate ?? "",
		notes: invoice.notes ?? "",
		lineItems: invoice.lineItems.map((item) => ({
			id: item.id,
			type: item.type,
			description: item.description,
			// API sends canonical decimal strings (e.g., "1234.56").
			// Convert to a locale-formatted string for the form input so that
			// parseCurrencyInput works correctly for both pt-BR and en-US.
			unitAmount: (() => {
				const numeric = Number(item.unitAmount);
				if (Number.isNaN(numeric)) {
					return item.unitAmount;
				}
				return new Intl.NumberFormat(locale).format(numeric);
			})(),
			quantity: item.quantity,
			enrollmentId: item.enrollmentId,
			// Currency consistency is validated at the invoice level; we don't
			// need per-line planCurrency when loading an existing invoice.
			planCurrency: undefined,
			// periodStart/periodEnd are CalendarDate ("YYYY-MM-DD") — the form input
			// format — so they pass straight through with no `new Date`.
			periodStart: item.periodStart ?? undefined,
			periodEnd: item.periodEnd ?? undefined,
		})),
	};
};

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/billing/new/")(
	{
		loader: async ({ context, params }) => {
			const { queryClient, userId, get_token } = context;
			const token = await get_token();
			// Prefetch provider accounts for payment step
			if (userId && token) {
				await queryClient.ensureQueryData(
					fetchProviderAccountsQueryOptions(userId, token, params.orgId),
				);
			}
		},
		validateSearch: billingNewSearchSchema,
		component: InvoiceCreationWizard,
	},
);

function InvoiceCreationWizard() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { orgId: organizationId } = Route.useParams();
	const search = Route.useSearch();
	const isExistingInvoiceMode = Boolean(search.invoiceId);
	const { getToken, userId } = useAuth();
	const [token, setToken] = useState<string | null>(null);
	const currentOrganization = useCurrentOrganization();

	useEffect(() => {
		getToken().then(setToken);
	}, [getToken]);

	// Wizard state - use organization default currency
	const wizard = useInvoiceWizard();
	const [wizardState, setWizardState] = useState<InvoiceWizardState>(() => {
		const base = createInitialWizardState(i18n.language);
		return {
			...base,
			invoice: {
				...base.invoice,
				currency: currentOrganization.defaultCurrency,
			},
		};
	});
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Fetch provider accounts for payments
	const { data: providerAccountsData } = useSuspenseQuery(
		fetchProviderAccountsQueryOptions(
			userId || "",
			token || "",
			organizationId,
		),
	);

	// Optionally fetch enrollment to prefill invoice when coming from enrollment flow
	const { data: enrollmentData } = useQuery({
		...fetchEnrollmentByIdQueryOptions(
			userId || "",
			token || "",
			organizationId,
			search.enrollmentId || "",
		),
		enabled: !!userId && !!token && !!search.enrollmentId,
	});
	// Optionally fetch existing invoice to prefill when coming from Record Payment flow
	const { data: existingInvoice } = useQuery({
		...fetchInvoiceByIdQueryOptions(
			userId || "",
			token || "",
			organizationId,
			search.invoiceId || "",
		),
		enabled: !!userId && !!token && !!search.invoiceId,
	});
	// Optionally fetch customer to prefill when coming from customer page
	const { data: customerData } = useQuery({
		...fetchCustomerByIdQueryOptions(
			userId || "",
			token || "",
			organizationId,
			search.customerId || "",
		),
		enabled:
			!!userId &&
			!!token &&
			!!search.customerId &&
			!search.enrollmentId &&
			!search.invoiceId,
	});

	// Mutations
	const createInvoiceMutation = useCreateInvoice();
	const createPaymentMutation = useCreateOfflinePayment();

	// Initialize invoice form from enrollment when available
	useEffect(() => {
		if (!enrollmentData) return;

		setWizardState((prev) => {
			// Avoid overwriting if user has already started editing
			if (prev.invoice.customerId || prev.invoice.lineItems.length > 0) {
				return prev;
			}

			const baseState = createInitialWizardState(i18n.language);

			// Renewal mode: use provided dates instead of enrollment's current dates.
			// All of these are CalendarDate ("YYYY-MM-DD") — the search params carry
			// the renewal period as day strings, and enrollment dates are already
			// CalendarDate — so no `new Date` / `toISOString` round-trip is needed.
			const isRenewal = search.renewal;
			const periodStart =
				isRenewal && search.periodStart
					? search.periodStart
					: enrollmentData.startDate;
			const periodEnd =
				isRenewal && search.periodEnd
					? search.periodEnd
					: (enrollmentData.endDate ?? undefined);

			// Format description for renewal with period dates
			// Convert i18n language format (pt_BR) to BCP 47 format (pt-BR)
			const locale = i18n.language.replace("_", "-");
			const description =
				isRenewal && periodStart && periodEnd
					? t("enrollments.renewal.description", {
							planName: enrollmentData.customerPlan.name,
							// UTC-pinned so the persisted description day is exact (P1-c).
							startDate: formatCalendarDayLocale(periodStart, locale),
							endDate: formatCalendarDayLocale(periodEnd, locale),
						})
					: enrollmentData.customerPlan.name;

			const currency = enrollmentData.customerPlan.currency as Currency;
			const currencyLocale = currency === Currency.BRL ? "pt-BR" : "en-US";

			const lineItem = {
				id: crypto.randomUUID(),
				type: InvoiceLineItemType.ENROLLMENT,
				description,
				unitAmount: (() => {
					const numeric = Number(enrollmentData.customerPlan.price);
					if (Number.isNaN(numeric)) {
						return enrollmentData.customerPlan.price.toString();
					}
					return new Intl.NumberFormat(currencyLocale, {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					}).format(numeric);
				})(),
				quantity: 1,
				enrollmentId: enrollmentData.id,
				planCurrency: enrollmentData.customerPlan.currency,
				periodStart,
				periodEnd,
			};

			return {
				invoice: {
					...baseState.invoice,
					customerId: enrollmentData.customer.id,
					customerName: `${enrollmentData.customer.firstName} ${enrollmentData.customer.lastName}`,
					customerEmail: enrollmentData.customer.email,
					currency,
					lineItems: [lineItem],
				},
				payments: baseState.payments,
			};
		});
	}, [
		enrollmentData,
		i18n.language,
		search.renewal,
		search.periodStart,
		search.periodEnd,
		t,
	]);
	// Initialize invoice form from existing invoice when available
	useEffect(() => {
		if (!existingInvoice) return;
		setWizardState((prev) => {
			// Avoid overwriting if user has already started editing
			if (prev.invoice.customerId || prev.invoice.lineItems.length > 0) {
				return prev;
			}
			return {
				...prev,
				invoice: mapInvoiceToFormData(existingInvoice),
			};
		});
	}, [existingInvoice]);

	// Initialize invoice form from customer when available (from customer page)
	useEffect(() => {
		if (!customerData) return;
		setWizardState((prev) => {
			// Avoid overwriting if user has already started editing
			if (prev.invoice.customerId || prev.invoice.lineItems.length > 0) {
				return prev;
			}
			return {
				...prev,
				invoice: {
					...prev.invoice,
					customerId: customerData.id,
					customerName: `${customerData.firstName} ${customerData.lastName}`,
					customerEmail: customerData.email,
				},
			};
		});
	}, [customerData]);

	// Update invoice form data
	const handleInvoiceFormChange = useCallback(
		(data: Partial<InvoiceFormData>) => {
			setWizardState((prev) => ({
				...prev,
				invoice: { ...prev.invoice, ...data },
			}));
		},
		[],
	);

	// Update payments
	const handlePaymentsChange = useCallback((payments: PaymentFormData[]) => {
		setWizardState((prev) => ({ ...prev, payments }));
	}, []);
	const locale = wizardState.invoice.currency === "BRL" ? "pt-BR" : "en-US";

	// Validate current step
	const canProceed = useCallback(() => {
		switch (wizard.currentStep) {
			case "invoice": {
				// Basic validation: at least one line item and all with description/unitAmount
				const hasValidLineItems =
					wizardState.invoice.lineItems.length > 0 &&
					wizardState.invoice.lineItems.every(
						(item) => item.description && item.unitAmount,
					);
				if (!hasValidLineItems) return false;

				// Currency validation: all line items must effectively use the same currency
				const invoiceCurrency = wizardState.invoice.currency;
				const uniqueCurrencies = new Set(
					wizardState.invoice.lineItems.map(
						(item) => item.planCurrency ?? invoiceCurrency,
					),
				);
				if (uniqueCurrencies.size > 1) return false;

				// Total amount validation: invoice total must be greater than 0
				const total = wizardState.invoice.lineItems.reduce((sum, item) => {
					const amount = parseCurrencyInput(
						item.unitAmount,
						wizardState.invoice.currency,
						locale,
					);
					if (Number.isNaN(amount)) return sum;

					const lineTotal = amount * item.quantity;
					const signedLineTotal =
						item.type === InvoiceLineItemType.DISCOUNT ? -lineTotal : lineTotal;

					return sum + signedLineTotal;
				}, 0);

				return total > 0;
			}
			case "payment": {
				// Payment step requires at least one payment with amount > 0
				// and the sum of all payments (existing + new) must match the invoice total
				// Derive locale from invoice currency
				const locale =
					wizardState.invoice.currency === "BRL" ? "pt-BR" : "en-US";

				// Calculate invoice total
				const invoiceTotal = wizardState.invoice.lineItems.reduce(
					(sum, item) => {
						const amount = parseCurrencyInput(
							item.unitAmount,
							wizardState.invoice.currency,
							locale,
						);
						if (Number.isNaN(amount)) return sum;

						const lineTotal = amount * item.quantity;
						const signedLineTotal =
							item.type === InvoiceLineItemType.DISCOUNT
								? -lineTotal
								: lineTotal;

						return sum + signedLineTotal;
					},
					0,
				);

				// Calculate already paid amount from existing invoice (if any)
				const alreadyPaid = existingInvoice
					? existingInvoice.payments
							.filter(
								(p) =>
									p.status === "SUCCEEDED" || p.status === "PARTIALLY_REFUNDED",
							)
							.reduce((sum, p) => {
								const paymentAmount = Number.parseFloat(p.amount);
								const refundedAmount = Number.parseFloat(
									p.refundedAmount || "0",
								);
								return sum + (paymentAmount - refundedAmount);
							}, 0)
					: 0;

				// Calculate new payments total and check if at least one payment has amount > 0
				let hasValidPayment = false;
				const newPaymentsTotal = wizardState.payments.reduce((sum, payment) => {
					const amount = parseCurrencyInput(
						payment.amount,
						wizardState.invoice.currency,
						locale,
					);
					if (!Number.isNaN(amount) && amount > 0) {
						hasValidPayment = true;
					}
					return sum + (Number.isNaN(amount) ? 0 : amount);
				}, 0);

				// Calculate total paid (existing + new)
				const totalPaid = alreadyPaid + newPaymentsTotal;

				// Check: at least one valid payment and totals match exactly
				const totalsMatch = totalPaid === invoiceTotal;
				return hasValidPayment && totalsMatch;
			}
			default:
				return false;
		}
	}, [
		wizard.currentStep,
		wizardState.invoice.currency,
		wizardState.invoice.lineItems,
		wizardState.payments,
		existingInvoice,
		locale,
	]);

	// Handle next step
	const handleNext = useCallback(() => {
		if (canProceed()) {
			wizard.goToNextStep();
		}
	}, [canProceed, wizard]);

	// Handle submit
	const handleSubmit = useCallback(async () => {
		if (!token) {
			console.error("Failed to submit invoice: No token");
			return;
		}

		// Validate that all payments have a provider account ID
		const invalidPayments = wizardState.payments.filter(
			(p) => !p.providerAccountId,
		);
		if (invalidPayments.length > 0) {
			console.error(
				"Failed to submit invoice: Some payments are missing provider account ID",
			);
			return;
		}

		setIsSubmitting(true);
		try {
			let targetInvoiceId = search.invoiceId;
			let targetCustomerId = wizardState.invoice.customerId;

			// When not coming from an existing invoice, create a new one first
			if (!targetInvoiceId) {
				const lineItems: API_CreateInvoiceLineItemRequest[] =
					wizardState.invoice.lineItems.map((item) => ({
						type: item.type,
						description: item.description,
						unitAmount: item.unitAmount,
						quantity: item.quantity,
						enrollmentId: item.enrollmentId, // [v3.8] Include enrollmentId for ENROLLMENT line items
						// The wizard holds the period as "YYYY-MM-DD" form strings;
						// parse them into CalendarDate at the request boundary.
						periodStart: item.periodStart
							? parseCalendarDate(item.periodStart, "periodStart")
							: undefined,
						periodEnd: item.periodEnd
							? parseCalendarDate(item.periodEnd, "periodEnd")
							: undefined,
					}));

				// Extract enrollmentId from enrollment line items (if single enrollment)
				const enrollmentLineItem = wizardState.invoice.lineItems.find(
					(item) => item.type === "ENROLLMENT" && item.enrollmentId,
				);

				const invoiceRequest: API_CreateInvoiceRequest = {
					customerId: wizardState.invoice.customerId,
					enrollmentId: enrollmentLineItem?.enrollmentId,
					currency: wizardState.invoice.currency,
					notes: wizardState.invoice.notes || undefined,
					lineItems,
				};

				const createdInvoice: Invoice = await createInvoiceMutation.mutateAsync(
					{
						token,
						organizationId,
						data: invoiceRequest,
					},
				);

				targetInvoiceId = createdInvoice.id;
				targetCustomerId = createdInvoice.customerId ?? targetCustomerId;
			}

			// 2. Create payments (if any)
			for (const payment of wizardState.payments) {
				const paymentRequest: API_CreateOfflinePaymentRequest = {
					invoiceId: targetInvoiceId,
					customerId: targetCustomerId,
					paymentProviderAccountId: payment.providerAccountId,
					amount: String(
						parseCurrencyInput(
							payment.amount,
							wizardState.invoice.currency,
							locale,
						),
					),
					paymentMethod: payment.paymentMethod as PaymentMethod,
					notes: payment.notes || undefined,
				};

				await createPaymentMutation.mutateAsync({
					token,
					organizationId,
					data: paymentRequest,
				});
			}

			// 3. Invalidate enrollment queries if invoice has enrollment line items
			const enrollmentIds = wizardState.invoice.lineItems
				.filter((item) => item.enrollmentId)
				.map((item) => item.enrollmentId as string);

			if (enrollmentIds.length > 0) {
				// Invalidate organization enrollments list
				queryClient.invalidateQueries({
					queryKey: ["organization_enrollments", organizationId],
				});

				// Invalidate each specific enrollment detail
				for (const enrollmentId of enrollmentIds) {
					queryClient.invalidateQueries({
						queryKey: ["enrollment", organizationId, enrollmentId],
					});
				}
			}

			// 4. Navigate to the relevant invoice (existing or newly created)
			if (targetInvoiceId) {
				navigate({
					to: "/app/org/$orgId/billing/$invoiceId/overview",
					params: { orgId: organizationId, invoiceId: targetInvoiceId },
				});
			}
		} catch {
			// Error handling is done by the mutation hooks
		} finally {
			setIsSubmitting(false);
		}
	}, [
		search.invoiceId,
		token,
		wizardState,
		locale,
		createInvoiceMutation,
		createPaymentMutation,
		organizationId,
		navigate,
		queryClient,
	]);

	// Render step content
	const renderStepContent = () => {
		switch (wizard.currentStep) {
			case "invoice":
				return isExistingInvoiceMode ? (
					<div className="pointer-events-none opacity-80">
						<InvoiceStep
							formData={wizardState.invoice}
							onFormDataChange={handleInvoiceFormChange}
						/>
					</div>
				) : (
					<InvoiceStep
						formData={wizardState.invoice}
						onFormDataChange={handleInvoiceFormChange}
					/>
				);
			case "payment":
				return (
					<PaymentStep
						invoiceData={wizardState.invoice}
						payments={wizardState.payments}
						onPaymentsChange={handlePaymentsChange}
						providerAccounts={providerAccountsData?.providerAccounts ?? []}
						existingInvoice={existingInvoice}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("billing.wizard.title")}
				description={t("billing.wizard.description")}
				showBackButton
				backButtonText={t("common.back")}
			/>

			<div className="flex-1 overflow-auto p-4 sm:p-6">
				<div className="max-w-3xl mx-auto space-y-6">
					{/* Stepper Header */}
					<InvoiceStepperHeader
						currentStep={wizard.currentStep}
						onStepChange={wizard.goToStep}
						completedSteps={wizard.completedSteps}
						canNavigateToStep={wizard.canNavigateToStep}
					/>

					{/* Step Content */}
					<div className="py-2">{renderStepContent()}</div>

					{/* Navigation */}
					<InvoiceStepperNavigation
						onBack={wizard.goToPreviousStep}
						onNext={handleNext}
						onSubmit={handleSubmit}
						isSubmitting={isSubmitting}
						canProceed={canProceed()}
						isFirstStep={wizard.isFirstStep}
						isLastStep={wizard.isLastStep}
					/>
				</div>
			</div>
		</div>
	);
}
